import { Button, Input, Spinner } from '@crm/ui';
import { Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { catalogService, useCreateService } from '@/modules/catalog';
import { useTemplates } from '@/modules/templates';

interface RascunhoServico {
  templateId: string;
  name: string;
  price: string;
  durationMinutes: string;
}

/**
 * Um wizard dentro do wizard: abre categoria por categoria (na ordem em que
 * foram criadas no passo anterior), ja com os servicos mais comuns dela
 * preenchidos - so nome, duracao e preco, sem foto. Confirmar uma fecha e
 * abre a proxima; a ultima conclui.
 */
export function ServicesStep({
  categoriaMapa,
  onDone,
}: {
  categoriaMapa: Record<string, string>;
  onDone: () => void;
}) {
  const templates = useTemplates();
  const createService = useCreateService();
  const [indiceCategoria, setIndiceCategoria] = useState(0);
  const [rascunhos, setRascunhos] = useState<Record<string, RascunhoServico[]> | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Trava sincrona: o `enviando` do state so vale no proximo render, e um
  // clique duplo rapido passa por ele. A ref barra na hora.
  const emAndamento = useRef(false);

  const categorias = (templates.data?.data ?? []).filter(
    (categoria) => categoriaMapa[categoria.id],
  );

  useEffect(() => {
    if (templates.data && rascunhos === null) {
      const inicial: Record<string, RascunhoServico[]> = {};
      for (const categoria of templates.data.data) {
        inicial[categoria.id] = categoria.services.map((servico) => ({
          templateId: servico.id,
          name: servico.name,
          price: String(servico.price),
          durationMinutes: String(servico.durationMinutes),
        }));
      }
      setRascunhos(inicial);
    }
  }, [templates.data, rascunhos]);

  if (templates.isLoading || rascunhos === null) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const categoriaAtual = categorias[indiceCategoria];

  // Sem mais categoria: ja passou por todas.
  if (!categoriaAtual) {
    onDone();
    return null;
  }

  const categoriaId = categoriaAtual.id;
  const lista = rascunhos[categoriaId] ?? [];
  const ultima = indiceCategoria + 1 >= categorias.length;

  function atualizar(indice: number, campo: Partial<RascunhoServico>) {
    setRascunhos((atual) => ({
      ...atual,
      [categoriaId]: (atual?.[categoriaId] ?? []).map((item, i) =>
        i === indice ? { ...item, ...campo } : item,
      ),
    }));
  }

  async function continuarCategoria() {
    if (emAndamento.current) return;

    emAndamento.current = true;
    setEnviando(true);
    setErro(null);

    try {
      const companyCategoryId = categoriaMapa[categoriaId];

      // Servico que ja existe nesta categoria (de uma visita anterior a
      // este mesmo passo, por exemplo) nao e recriado.
      const existentes = await catalogService.listServices({
        page: 1,
        perPage: 100,
        categoryId: companyCategoryId,
      });
      const nomesExistentes = new Set(
        existentes.data.map((servico) => servico.name.trim().toLowerCase()),
      );

      for (const servico of lista) {
        if (nomesExistentes.has(servico.name.trim().toLowerCase())) continue;

        await createService.mutateAsync({
          name: servico.name,
          categoryId: companyCategoryId,
          price: Number(servico.price) || 0,
          durationMinutes: Number(servico.durationMinutes) || 30,
          images: [],
          isActive: true,
          maxDiscountPercent: 0,
          autoDiscountPercent: 0,
        });
      }

      if (ultima) onDone();
      else setIndiceCategoria((i) => i + 1);
    } catch (error) {
      setErro(toErrorMessage(error));
    } finally {
      emAndamento.current = false;
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-base font-semibold text-foreground">
        Categoria {indiceCategoria + 1} de {categorias.length}:{' '}
        <span className="text-primary">{categoriaAtual.name}</span>
      </p>

      {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

      {lista.length === 0 ? (
        <p className="rounded-card border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          Nenhum servico pronto para esta categoria.
        </p>
      ) : (
        <div className="space-y-2">
          {lista.map((servico, indice) => (
            <div
              key={servico.templateId}
              className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-card p-2.5"
            >
              <Input
                aria-label="Nome do servico"
                className="min-w-[160px] flex-1"
                value={servico.name}
                onChange={(event) => atualizar(indice, { name: event.target.value })}
              />

              <Input
                aria-label="Minutos"
                type="number"
                className="w-20"
                value={servico.durationMinutes}
                onChange={(event) => atualizar(indice, { durationMinutes: event.target.value })}
              />

              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">R$</span>
                <Input
                  aria-label="Preco"
                  type="number"
                  className="w-24"
                  value={servico.price}
                  onChange={(event) => atualizar(indice, { price: event.target.value })}
                />
              </div>

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remover ${servico.name}`}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  setRascunhos((atual) => ({
                    ...atual,
                    [categoriaId]: (atual?.[categoriaId] ?? []).filter((_, i) => i !== indice),
                  }))
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={continuarCategoria} disabled={enviando}>
          {enviando ? <Spinner className="h-4 w-4" /> : null}
          {ultima ? 'Concluir' : 'Proxima categoria'}
        </Button>
      </div>
    </div>
  );
}
